import { showApiErrorToast } from "@/common/helpers/show-api-error-toast";
import { toast } from "sonner";

interface CustomErrorMapping {
  [statusCode: number]: string | null;
}

interface ToastMutationError {
  status?: number;
  response?: {
    status?: number;
    data?: { message?: string };
  };
}

interface ToastMutationStatusParams<T> {
  mutationFn: () => Promise<T>;
  successMessage?: string;
  errorMessage: string;
  customErrors?: CustomErrorMapping;
  resolveErrorMessage?: (error: unknown, fallbackMessage: string) => string | null;
  onSuccess?: (result: T) => void;
}

function getToastErrorMessage({
  backendMessage,
  customErrors,
  errorMessage,
  errorStatus,
  resolvedErrorMessage,
}: {
  backendMessage?: string;
  customErrors?: CustomErrorMapping;
  errorMessage: string;
  errorStatus?: number;
  resolvedErrorMessage: string | null;
}): string {
  if (customErrors && errorStatus && errorStatus in customErrors) {
    const customResolvedErrorMessage =
      resolvedErrorMessage && resolvedErrorMessage !== errorMessage ? resolvedErrorMessage : null;

    return customErrors[errorStatus] ?? customResolvedErrorMessage ?? backendMessage ?? errorMessage;
  }

  return resolvedErrorMessage ?? errorMessage;
}

export async function toastMutationStatus<T>({
  mutationFn,
  successMessage,
  errorMessage,
  customErrors,
  resolveErrorMessage,
  onSuccess,
}: ToastMutationStatusParams<T>) {
  try {
    const result = await mutationFn();

    if (successMessage) {
      toast.success(successMessage);
    }

    onSuccess?.(result);
  } catch (_error: unknown) {
    const error = _error as ToastMutationError;

    const errorStatus = error.status ?? error.response?.status;
    const backendMessage = error.response?.data?.message;
    const resolvedErrorMessage = resolveErrorMessage?.(_error, errorMessage) ?? null;

    showApiErrorToast(
      getToastErrorMessage({
        backendMessage,
        customErrors,
        errorMessage,
        errorStatus,
        resolvedErrorMessage,
      }),
      _error,
    );
  }
}
